import { useState, useContext, useEffect, useMemo } from "react";
import { Trash2, Plus } from "lucide-react";
import { ApiContext } from "../../../../context/MyContext";
import { useUtils } from "../../../../context/UtilsContext";
import Button from "../../../../common/Button/Button";
import { modifyEmployeeRoles } from "../../../../service/employeeService";
import { resolveModifyRoleEventTypeId } from "../resolveModifyRoleEventTypeId";
import {
  getApiErrorMessage,
  getSessionEventTypes,
  isRoleCurrent,
} from "../employeeListUtils";

function createEmptyRole(isPrimary = false) {
  return {
    id: `${Date.now()}-${Math.random()}`,
    department: "",
    roleId: "",
    isPrimary,
    activeFrom: null,
    activeTo: null,
  };
}

function resolveRoleId(departments, departmentName, roleTitle) {
  const dept = departments.find((d) => d.name === departmentName);
  return dept?.roles?.find((r) => r.title === roleTitle)?.id || "";
}

function hasDuplicateRoleIds(roleList) {
  const seen = new Set();
  for (const role of roleList) {
    if (!role.roleId) continue;
    if (seen.has(role.roleId)) return true;
    seen.add(role.roleId);
  }
  return false;
}

const UpdateRoleModal = ({
  employee,
  departments,
  onClose,
  onUpdateSuccess,
  onToast,
}) => {
  const { post } = useContext(ApiContext);
  const { sessionUtils } = useUtils();
  const employeeId =
    employee.employee_id || employee.id || employee.user_id || null;

  const [roles, setRoles] = useState([createEmptyRole(true)]);
  const [loading, setLoading] = useState(false);

  const roleEventTypeId = useMemo(
    () => resolveModifyRoleEventTypeId(getSessionEventTypes(sessionUtils)),
    [sessionUtils]
  );

  const selectedRoleIds = useMemo(
    () => new Set(roles.map((r) => r.roleId).filter(Boolean)),
    [roles]
  );

  useEffect(() => {
    const current = employee.current_roles || [];
    if (current.length === 0) {
      setRoles([createEmptyRole(true)]);
      return;
    }

    const seen = new Set();
    const mapped = [];
    current.forEach((role, index) => {
      const department = role.department_title || role.department || "";
      const title = role.role_title || role.role || "";
      const roleId =
        role.role_id || resolveRoleId(departments, department, title);
      if (roleId && seen.has(roleId)) return;
      if (roleId) seen.add(roleId);

      mapped.push({
        id: `${Date.now()}-${index}`,
        department,
        roleId: roleId || "",
        isPrimary: role.is_primary ?? mapped.length === 0,
        activeFrom: role.active_from || null,
        activeTo: role.active_to || null,
      });
    });

    if (mapped.length && !mapped.some((r) => r.isPrimary)) {
      mapped[0].isPrimary = true;
    }
    setRoles(mapped.length ? mapped : [createEmptyRole(true)]);
  }, [employee.current_roles, departments]);

  const updateRoleField = (id, field, value) => {
    if (field === "roleId" && value) {
      const taken = roles.some((r) => r.id !== id && r.roleId === value);
      if (taken) {
        onToast("That role is already assigned. Choose a different one.", "error");
        return;
      }
    }

    setRoles((prev) =>
      prev.map((role) => {
        if (role.id !== id) {
          return field === "isPrimary" && value
            ? { ...role, isPrimary: false }
            : role;
        }
        if (field === "department") {
          return { ...role, department: value, roleId: "" };
        }
        if (field === "roleId") return { ...role, roleId: value };
        if (field === "isPrimary") return { ...role, isPrimary: value };
        return role;
      })
    );
  };

  const buildRoleAssignments = () => {
    const now = new Date().toISOString();
    // The same predicate `current_roles` was built with, not `!active_to`. Those
    // agreed only while any end date meant "gone"; a role ending next month is a
    // live assignment to UPDATE, and excluding it here would post an ADD for a
    // role the employee already holds — and never emit its DELETE.
    const activeAssignments = (employee.current_roles || [])
      .filter((role) => isRoleCurrent(role))
      .map((role) => ({
        assignmentId:
          role.employee_role_id || role.role_assignment_id || role.id || null,
        roleId:
          role.role_id ||
          resolveRoleId(
            departments,
            role.department_title || role.department || "",
            role.role_title || role.role || ""
          ),
        active_from: role.active_from,
        active_to: role.active_to,
      }));

    const assignments = [];
    const postedRoleIds = new Set();
    const keptAssignmentIds = new Set();

    roles.forEach((role, index) => {
      if (!role.roleId || postedRoleIds.has(role.roleId)) return;
      postedRoleIds.add(role.roleId);

      const match = activeAssignments.find(
        (existing) =>
          existing.roleId === role.roleId &&
          !keptAssignmentIds.has(existing.assignmentId)
      );
      const base = {
        employee_id: employeeId,
        role_id: role.roleId,
        active_from: role.activeFrom || match?.active_from || now,
        active_to: role.activeTo || match?.active_to || null,
        is_primary: role.isPrimary ?? index === 0,
      };

      if (match?.assignmentId) {
        keptAssignmentIds.add(match.assignmentId);
        assignments.push({ ...base, id: match.assignmentId, operation: "UPDATE" });
      } else {
        assignments.push({ ...base, operation: "ADD" });
      }
    });

    activeAssignments.forEach((existing) => {
      if (!existing.assignmentId || keptAssignmentIds.has(existing.assignmentId)) {
        return;
      }
      assignments.push({
        operation: "DELETE",
        id: existing.assignmentId,
        employee_id: employeeId,
      });
    });

    return assignments;
  };

  const handleUpdateRole = async () => {
    if (roles.length > 0) {
      if (roles.some((r) => !r.department || !r.roleId)) {
        onToast("Please select both department and role for each entry", "error");
        return;
      }
      if (hasDuplicateRoleIds(roles)) {
        onToast(
          "Duplicate roles are not allowed. Each role can only be assigned once.",
          "error"
        );
        return;
      }
      if (!roles.some((r) => r.isPrimary)) {
        onToast("Please select at least one role as primary", "error");
        return;
      }
    }

    if (!employeeId) {
      onToast("Employee id is missing. Please refresh and try again.", "error");
      return;
    }
    if (!roleEventTypeId) {
      onToast("Event type for modifying roles is missing.", "error");
      return;
    }

    const roleAssignments = buildRoleAssignments();
    if (!roleAssignments.length) {
      onToast("Please add at least one role to update.", "error");
      return;
    }

    try {
      setLoading(true);
      await modifyEmployeeRoles(post, {
        event_type_id: roleEventTypeId,
        role_assignments: roleAssignments,
      });
      onToast("Employee Role Updated!", "success");
      onUpdateSuccess();
    } catch (error) {
      console.error("Update failed:", error.message);
      onToast(getApiErrorMessage(error, "Failed to update roles"), "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-orange-primary bg-opacity-20 flex justify-center items-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white p-6 rounded-lg shadow-lg w-[700px] min-w-[600px] max-w-[90vw] max-h-[90vh] overflow-y-auto border border-orange-200"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-semibold mb-4">
          Manage Roles - {employee.name}
        </h2>

        <div className="space-y-4 mb-4">
          {roles.map((roleData, index) => {
            const availableRoles =
              departments.find((d) => d.name === roleData.department)?.roles ||
              [];

            return (
              <div
                key={roleData.id}
                className="p-4 border border-orange-200 rounded-lg bg-orange-50/40"
              >
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold">
                    Role #{index + 1}
                    {roleData.isPrimary && (
                      <span className="ml-2 text-xs bg-orange-500 text-white px-2 py-1 rounded">
                        Primary
                      </span>
                    )}
                  </h3>
                  {roles.length > 1 && (
                    <button
                      type="button"
                      aria-label={`Remove role ${index + 1}`}
                      className="text-red-500 hover:text-red-700"
                      onClick={() =>
                        setRoles((prev) => prev.filter((r) => r.id !== roleData.id))
                      }
                    >
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>

                <label className="block text-sm font-semibold mb-1 text-orange-800">
                  Department *
                </label>
                <select
                  className="w-full p-2 mb-3 border border-orange-300 rounded-md focus:ring-2 focus:ring-orange-400 focus:border-orange-500"
                  value={roleData.department}
                  onChange={(e) =>
                    updateRoleField(roleData.id, "department", e.target.value)
                  }
                >
                  <option value="">-- Select Department --</option>
                  {departments.map((dept) => (
                    <option key={dept.id} value={dept.name}>
                      {dept.name}
                    </option>
                  ))}
                </select>

                {availableRoles.length > 0 && (
                  <>
                    <label className="block text-sm font-semibold mb-1 text-orange-800">
                      Role *
                    </label>
                    <select
                      className="w-full p-2 mb-3 border border-orange-300 rounded-md focus:ring-2 focus:ring-orange-400 focus:border-orange-500"
                      value={roleData.roleId}
                      onChange={(e) =>
                        updateRoleField(roleData.id, "roleId", e.target.value)
                      }
                    >
                      <option value="">-- Select Role --</option>
                      {availableRoles.map((role) => {
                        const taken =
                          selectedRoleIds.has(role.id) &&
                          roleData.roleId !== role.id;
                        return (
                          <option key={role.id} value={role.id} disabled={taken}>
                            {role.title}
                            {taken ? " (already assigned)" : ""}
                          </option>
                        );
                      })}
                    </select>
                  </>
                )}

                <label className="inline-flex items-center gap-2 text-sm text-orange-800 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={roleData.isPrimary}
                    onChange={(e) =>
                      updateRoleField(roleData.id, "isPrimary", e.target.checked)
                    }
                    className="w-4 h-4 accent-orange-500"
                  />
                  Set as Primary Role
                </label>
              </div>
            );
          })}
        </div>

        <button
          type="button"
          disabled={loading}
          onClick={() =>
            setRoles((prev) => [...prev, createEmptyRole(prev.length === 0)])
          }
          className="w-full mb-4 px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 flex items-center justify-center gap-2"
        >
          <Plus size={18} />
          Add Another Role
        </button>

        <div className="flex justify-end space-x-2">
          <Button
            text="Cancel"
            className="bg-gray-300 text-gray-800 px-4 py-2 rounded-md"
            onClick={onClose}
            disabled={loading}
          />
          <Button
            text={loading ? "Updating..." : "Update Roles"}
            className={`px-4 py-2 rounded-md ${
              loading
                ? "bg-gray-300 text-gray-800 cursor-not-allowed"
                : "bg-orange-primary text-white"
            }`}
            disabled={loading}
            onClick={handleUpdateRole}
          />
        </div>
      </div>
    </div>
  );
};

export default UpdateRoleModal;
